import {
  FormEvent,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "../hooks/useAuth";
import * as accountService from "../services/accountService";
import * as paymentService from "../services/paymentService";
import type {
  Account,
  AccountTransfer,
  AccountTransaction,
  Payment,
  PaymentMethod,
  CreatePaymentPayload,
  ConfirmPaymentPayload,
  WithdrawalRequestPayload,
} from "../types/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { useToast } from "../components/ui/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import DepositFormModal from "../components/DepositFormModal";
import WithdrawFormModal from "../components/WithdrawFormModal";
import type { PaymentFormData } from "../components/PaymentFormModal";
import StripePaymentModal from "../components/StripePaymentModal";
import { Pagination } from "../components/table/DataTable";

const AccountsPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [transfers, setTransfers] = useState<AccountTransfer[]>([]);
  const [accountTransactions, setAccountTransactions] = useState<
    AccountTransaction[]
  >([]);
  const [accountTransactionsPage, setAccountTransactionsPage] = useState(1);
  const [transfersPage, setTransfersPage] = useState(1);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    direction: "main_to_trading" as "main_to_trading" | "trading_to_main",
    amount: "",
    currency: "USD",
  });
  const [transferring, setTransferring] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [currentPayment, setCurrentPayment] = useState<Payment | null>(null);
  const [stripePayment, setStripePayment] = useState<Payment | null>(null);
  const [confirmationValue, setConfirmationValue] = useState("");
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const mainTransactionsRef = useRef<HTMLDivElement>(null);
  const transfersSectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const getPaymentErrorMessage = (error: unknown) => {
    if (typeof error === "string") {
      const lowered = error.toLowerCase();
      if (lowered.includes("wallet crypto") || lowered.includes("adresse de réception")) {
        return t(
          "payment.form.cryptoWalletMissing",
          "L'adresse du réseau choisi n'est pas configurée."
        );
      }
      return error;
    }
    return t("accounts.paymentInitErrorDescription");
  };
  const resolveCryptoNetworkLabel = (payment?: Payment | null) => {
    if (!payment?.metadata) return null;
    try {
      const parsed = JSON.parse(payment.metadata);
      const network =
        typeof parsed?.cryptoNetwork === "string"
          ? parsed.cryptoNetwork.toUpperCase()
          : null;
      const asset =
        typeof parsed?.cryptoAsset === "string"
          ? parsed.cryptoAsset.toUpperCase()
          : null;
      if (asset === "ETH" || network === "ERC20") {
        return t("payment.form.cryptoNetworkEthLabel", "ERC20 (ETH)");
      }
      if (asset === "BNB" || network === "BSC") {
        return t("payment.form.cryptoNetworkBnbLabel", "BSC (BEP20)");
      }
      if (asset === "BTC" || network === "BTC") {
        return t("payment.form.cryptoNetworkBtcLabel", "BTC (mainnet)");
      }
      return network;
    } catch {
      return null;
    }
  };
  const transfersPerPage = 5;
  const accountTransactionsPerPage = 6;
  const totalTransfersPages = Math.max(
    1,
    Math.ceil(transfers.length / transfersPerPage)
  );
  const totalAccountTransactionsPages = Math.max(
    1,
    Math.ceil(accountTransactions.length / accountTransactionsPerPage)
  );
  const paginatedTransfers = useMemo(() => {
    if (transfers.length === 0) return [];
    const start = (transfersPage - 1) * transfersPerPage;
    return transfers.slice(start, start + transfersPerPage);
  }, [transfers, transfersPage, transfersPerPage]);
  const paginatedAccountTransactions = useMemo(() => {
    if (accountTransactions.length === 0) return [];
    const start = (accountTransactionsPage - 1) * accountTransactionsPerPage;
    return accountTransactions.slice(start, start + accountTransactionsPerPage);
  }, [accountTransactions, accountTransactionsPage, accountTransactionsPerPage]);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await accountService.getUserAccounts();
      setAccounts(data);
    } catch (error) {
      toast({
        title: t("accounts.transferErrorTitle"),
        description:
          typeof error === "string"
            ? error
            : t(
                "accounts.loadingAccountsError",
                "Impossible de charger les comptes"
              ),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  const fetchAccountTransactions = useCallback(
    async (accountId?: number) => {
      if (!accountId) return;
      try {
        const data = await accountService.getAccountTransactions(
          accountId,
          50,
          0
        );
        setAccountTransactions(data);
      } catch {
        setAccountTransactions([]);
      }
    },
    []
  );

  const fetchTransfers = useCallback(async () => {
    try {
      const data = await accountService.getUserTransfers();
      setTransfers(data);
    } catch (error) {
      // Erreur silencieuse pour les transferts
    }
  }, []);

  useEffect(() => {
    if (user) {
      void fetchAccounts();
      void fetchTransfers();
    }
  }, [user, fetchAccounts, fetchTransfers]);

  useEffect(() => {
    if (transfersPage > totalTransfersPages) {
      setTransfersPage(totalTransfersPages);
    }
  }, [transfersPage, totalTransfersPages]);

  useEffect(() => {
    if (accountTransactionsPage > totalAccountTransactionsPages) {
      setAccountTransactionsPage(totalAccountTransactionsPages);
    }
  }, [accountTransactionsPage, totalAccountTransactionsPages]);

  const scrollToMainHistory = useCallback(() => {
    mainTransactionsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const scrollToTransfersHistory = useCallback(() => {
    transfersSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const handleDeposit = async (
    data: PaymentFormData & { amount: number; currency: string }
  ) => {
    if (!user) return;

    try {
      setIsSubmittingPayment(true);
      // Le backend n'a besoin que de amount, currency, method et metadata
      // Les informations de carte sont collectées mais stockées localement pour validation
      const payment = await paymentService.createPayment({
        amount: data.amount,
        currency: data.currency.toUpperCase(),
        method: data.method,
        metadata: JSON.stringify({
          cardholderName: data.cardholderName || null,
          billingCountry: data.country || null,
          cryptoAsset: data.method === "crypto" ? data.cryptoAsset : null,
          cryptoNetwork: data.method === "crypto" ? data.cryptoNetwork : null,
        }),
      });
      setDepositModalOpen(false);

      if (payment.method === "card" && payment.clientSecret) {
        setStripePayment(payment);
      } else {
        setCurrentPayment(payment);
      }

      toast({
        title: t("accounts.paymentInitTitle"),
        description: t("accounts.paymentInitDescription"),
        variant: "info",
      });
    } catch (error) {
      toast({
        title: t("accounts.transferErrorTitle"),
        description: getPaymentErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!currentPayment) return;

    // Pour les paiements crypto, pas besoin de confirmationValue
    if (currentPayment.method === "card" && !confirmationValue.trim()) {
      toast({
        title: t("accounts.paymentConfirmErrorTitle", "Code requis"),
        description: t("accounts.paymentConfirmErrorDescription", "Veuillez entrer le code de vérification."),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsConfirmingPayment(true);
      const payload: ConfirmPaymentPayload = {
        confirmationValue: currentPayment.method === "crypto" ? "" : confirmationValue,
      };

      const payment = await paymentService.confirmPayment(
        currentPayment.id,
        payload
      );
      setCurrentPayment(payment);

      if (payment.status === "confirmed") {
        toast({
          title: t("accounts.paymentConfirmTitle"),
          description: t("accounts.paymentConfirmDescription"),
          variant: "success",
        });
        setDepositModalOpen(false);
        setCurrentPayment(null);
        setConfirmationValue("");
        // Attendre un peu pour que le backend mette à jour le solde
        await new Promise((resolve) => setTimeout(resolve, 500));
        await fetchAccounts();
      } else if (payment.status === "requires_action") {
        toast({
          title: t("accounts.cryptoPaymentSubmittedTitle", "Paiement en attente"),
          description: t("accounts.cryptoPaymentSubmittedDescription", "Votre paiement a été confirmé. Un code unique a été envoyé à votre manager pour validation. Votre compte sera crédité après approbation."),
          variant: "info",
        });
        // Fermer le modal même si le paiement est en attente
        setDepositModalOpen(false);
        setCurrentPayment(null);
        setConfirmationValue("");
        await fetchAccounts();
      } else {
        toast({
          title: t("accounts.paymentPendingTitle"),
          description: t("accounts.paymentPendingDescription"),
          variant: "info",
        });
        // Fermer le modal aussi dans ce cas
        setCurrentPayment(null);
        setConfirmationValue("");
      }
    } catch (error) {
      toast({
        title: t("accounts.transferErrorTitle"),
        description:
          typeof error === "string"
            ? error
            : t("accounts.paymentConfirmErrorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsConfirmingPayment(false);
    }
  };

  const handleStripeSuccess = async (paymentIntentId: string) => {
    if (!stripePayment) return;
    try {
      const payment = await paymentService.confirmPayment(stripePayment.id, {
        confirmationValue: paymentIntentId,
      });
      setStripePayment(null);
      if (payment.status === "confirmed") {
        toast({
          title: t("accounts.paymentConfirmTitle"),
          description: t("accounts.paymentConfirmDescription"),
          variant: "success",
        });
        await new Promise((resolve) => setTimeout(resolve, 500));
        await fetchAccounts();
        await fetchAccountTransactions(mainAccount?.id);
      }
    } catch (error) {
      toast({
        title: t("accounts.transferErrorTitle"),
        description:
          typeof error === "string"
            ? error
            : t("accounts.paymentConfirmErrorDescription"),
        variant: "destructive",
      });
    }
  };

  const paymentInstructions = useMemo(() => {
    if (!currentPayment) return null;

    if (currentPayment.method === "card") {
      return (
        <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4 dark:border-white/10 dark:bg-white/5">
          <p className="text-sm font-medium text-foreground dark:text-white">
            {t("accounts.cardPaymentTitle")}
          </p>
          {currentPayment.paymentUrl && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground dark:text-white/60">
                {t(
                  "accounts.cardPaymentText",
                  "Cliquez sur le lien ci-dessous pour compléter votre paiement :"
                )}
              </p>
              <a
                href={currentPayment.paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-lg bg-cyan-500 px-4 py-2 text-center text-sm font-medium text-white hover:bg-cyan-600">
                {t("accounts.cardPaymentLinkText")}
              </a>
            </div>
          )}
        </div>
      );
    }

    // Crypto payment
    const cryptoNetwork = resolveCryptoNetworkLabel(currentPayment);
    return (
      <div className="space-y-3 rounded-lg border border-border bg-muted/40 p-4 dark:border-white/10 dark:bg-white/5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium text-foreground dark:text-white">
            {t("accounts.cryptoPaymentTitle")}
          </p>
          {cryptoNetwork && (
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] uppercase text-muted-foreground dark:border-white/10 dark:bg-white/10 dark:text-white/70">
              {cryptoNetwork}
            </span>
          )}
        </div>
        <p className="text-xs text-amber-800 dark:text-amber-300/90">
          {t(
            "payment.form.cryptoNetworkWarning",
            "⚠️ Sélectionnez exactement le même réseau que celui de votre wallet (ex: Binance), sinon les fonds peuvent être perdus."
          )}
        </p>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground dark:text-white/60">
            {t("accounts.cryptoPaymentAmountLabel")}
          </p>
          <p className="text-lg font-semibold text-foreground dark:text-white">
            {currentPayment.amount} {currentPayment.currency}
          </p>
        </div>
        {currentPayment.walletAddress && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground dark:text-white/60">
              {t("accounts.cryptoPaymentWalletLabel")}
            </p>
            <div className="flex items-center gap-2">
              <code className="block flex-1 break-all rounded bg-muted p-2 text-xs text-foreground dark:bg-black/30 dark:text-white">
                {currentPayment.walletAddress}
              </code>
              <Button
                type="button"
                variant="ghost"
                aria-label={t("accounts.copyButton", "Copier")}
                className="h-8 w-8 shrink-0 rounded-md border border-border bg-muted/60 p-0 text-muted-foreground hover:bg-muted hover:text-foreground dark:border-white/10 dark:bg-white/5 dark:text-white/80 dark:hover:bg-white/10 dark:hover:text-white"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(
                      currentPayment.walletAddress || ""
                    );
                    toast({
                      title: t("accounts.copySuccessTitle", "Copié"),
                      description: t(
                        "accounts.copySuccessDescription",
                        "Adresse copiée."
                      ),
                    });
                  } catch {
                    toast({
                      title: t("accounts.copyErrorTitle", "Erreur"),
                      description: t(
                        "accounts.copyErrorDescription",
                        "Impossible de copier l'adresse."
                      ),
                      variant: "destructive",
                    });
                  }
                }}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(
                currentPayment.walletAddress
              )}`}
              alt={t("accounts.copyButton", "Copier")}
              className="h-24 w-24 rounded-md bg-white p-1"
            />
          </div>
        )}
      </div>
    );
  }, [currentPayment]);

  const handleTransfer = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return;

    const amount = Number.parseFloat(transferForm.amount);
    if (Number.isNaN(amount) || amount <= 0) {
      toast({
        title: t("accounts.transferErrorTitle"),
        description: t("accounts.transferAmountInvalid"),
        variant: "destructive",
      });
      return;
    }

    try {
      setTransferring(true);
      await accountService.transferBetweenAccounts({
        direction: transferForm.direction,
        amount,
        currency: transferForm.currency,
      });
      toast({
        title: t("accounts.transferSuccessTitle"),
        description: t("accounts.transferSuccessDescription"),
        variant: "success",
      });
      setTransferModalOpen(false);
      setTransferForm({
        direction: "main_to_trading",
        amount: "",
        currency: "USD",
      });
      await fetchAccounts();
      await fetchTransfers();
      await fetchAccountTransactions(mainAccount?.id);
    } catch (error) {
      toast({
        title: t("accounts.transferErrorTitle"),
        description:
          typeof error === "string"
            ? error
            : t("accounts.transferErrorDescription"),
        variant: "destructive",
      });
    } finally {
      setTransferring(false);
    }
  };

  const mainAccount = accounts.find(
    (a) => a.type === "main" && a.currency === "USD"
  );
  const tradingAccount = accounts.find(
    (a) => a.type === "trading" && a.currency === "USD"
  );

  useEffect(() => {
    if (mainAccount?.id) {
      void fetchAccountTransactions(mainAccount.id);
    }
  }, [fetchAccountTransactions, mainAccount?.id]);

  const handleWithdraw = async (payload: WithdrawalRequestPayload) => {
    if (!user) return;

    try {
      setWithdrawing(true);
      await accountService.requestWithdrawal(payload);

      toast({
        title: t("accounts.withdrawSuccessTitle"),
        description: t("accounts.withdrawSuccessDescription"),
        variant: "success",
      });
      setWithdrawModalOpen(false);
      await fetchAccounts();
      await fetchAccountTransactions(mainAccount?.id);
    } catch (error) {
      toast({
        title: t("accounts.withdrawErrorTitle"),
        description:
          typeof error === "string"
            ? error
            : t("accounts.withdrawErrorDescription"),
        variant: "destructive",
      });
      throw error;
    } finally {
      setWithdrawing(false);
    }
  };

  const availableMainBalance = mainAccount
    ? mainAccount.balance - mainAccount.lockedBalance
    : 0;
  const availableTradingBalance = tradingAccount
    ? tradingAccount.balance - tradingAccount.lockedBalance
    : 0;

  const formatAccountTransactionType = (type: AccountTransaction["type"]) => {
    switch (type) {
      case "deposit":
        return t("accounts.transactionTypeDeposit");
      case "withdrawal":
        return t("accounts.transactionTypeWithdrawal");
      case "transfer_in":
        return t("accounts.transactionTypeTransferIn");
      case "transfer_out":
        return t("accounts.transactionTypeTransferOut");
      case "credit":
        return t("accounts.transactionTypeCredit");
      case "debit":
        return t("accounts.transactionTypeDebit");
      default:
        return t("accounts.transactionTypeOther");
    }
  };

  const getWithdrawalDestination = (
    metadata: AccountTransaction["metadata"]
  ) => {
    if (!metadata) return null;
    try {
      const parsed = JSON.parse(metadata) as {
        destination?: string | null;
      };
      return parsed.destination ?? null;
    } catch {
      return null;
    }
  };

  if (!user) {
    return <p>{t("accounts.loginRequired")}</p>;
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold text-foreground">
          {t("accounts.title")}
        </h2>
        <p className="text-sm text-muted-foreground dark:text-white/70">{t("accounts.subtitle")}</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Compte principal */}
        <div className="rounded-3xl border border-border bg-muted/50 p-6 text-foreground dark:border-white/10 dark:bg-black/30 dark:text-white">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">
                {t("accounts.mainAccountTitle")}
              </h3>
              <p className="text-sm text-muted-foreground dark:text-white/60">USD</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={scrollToMainHistory}
                aria-label={t(
                  "accounts.historyScrollMainAria",
                  "Voir l'historique du compte principal"
                )}>
                {t("accounts.history")}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setDepositModalOpen(true);
                  setCurrentPayment(null);
                  setConfirmationValue("");
                }}
                className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white hover:from-cyan-500 hover:to-blue-600">
                {t("accounts.recharge")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setWithdrawModalOpen(true)}>
                {t("accounts.withdrawButton")}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground dark:text-white/60">
                {t("accounts.totalBalance")}
              </span>
              <span className="text-lg font-semibold">
                {mainAccount?.balance.toFixed(2) ?? "0.00"} $
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground dark:text-white/60">{t("accounts.available")}</span>
              <span className="text-emerald-600 dark:text-emerald-400">
                {availableMainBalance.toFixed(2)} $
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground dark:text-white/60">{t("accounts.locked")}</span>
              <span className="text-muted-foreground dark:text-white/40">
                {mainAccount?.lockedBalance.toFixed(2) ?? "0.00"} $
              </span>
            </div>
          </div>
        </div>

        {/* Compte trading */}
        <div className="rounded-3xl border border-border bg-muted/50 p-6 text-foreground dark:border-white/10 dark:bg-black/30 dark:text-white">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold">
                {t("accounts.tradingAccountTitle")}
              </h3>
              <p className="text-sm text-muted-foreground dark:text-white/60">USD</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={scrollToTransfersHistory}
              aria-label={t(
                "accounts.historyScrollTradingAria",
                "Voir l'historique des transferts entre comptes"
              )}>
              {t("accounts.history")}
            </Button>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground dark:text-white/60">Solde total</span>
              <span className="text-lg font-semibold">
                {tradingAccount?.balance.toFixed(2) ?? "0.00"} $
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground dark:text-white/60">Disponible</span>
              <span className="text-emerald-600 dark:text-emerald-400">
                {availableTradingBalance.toFixed(2)} $
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground dark:text-white/60">Verrouillé</span>
              <span className="text-muted-foreground dark:text-white/40">
                {tradingAccount?.lockedBalance.toFixed(2) ?? "0.00"} $
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Bouton de transfert */}
      <div className="flex justify-center">
        <Button
          onClick={() => setTransferModalOpen(true)}
          className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600">
          {t("accounts.transferButton")}
        </Button>
      </div>

      {/* Historique du compte principal */}
      <div
        ref={mainTransactionsRef}
        className="rounded-3xl border border-border bg-muted/50 p-6 text-foreground dark:border-white/10 dark:bg-black/30 dark:text-white">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {t("accounts.transactionsTitle")}
          </h3>
          <span className="text-xs text-muted-foreground dark:text-white/50">
            {t("accounts.transactionsSubtitle")}
          </span>
        </div>
        {paginatedAccountTransactions.length === 0 ? (
          <p className="text-sm text-muted-foreground dark:text-white/60">
            {t("accounts.transactionsEmpty")}
          </p>
        ) : (
          <div className="space-y-3">
            {paginatedAccountTransactions.map((tx) => {
              const isPositive = ["deposit", "credit", "transfer_in"].includes(
                tx.type
              );
              const destination =
                tx.referenceType === "withdrawal"
                  ? getWithdrawalDestination(tx.metadata)
                  : null;
              return (
                <div
                  key={tx.id}
                  className="flex items-center justify-between rounded-lg border border-border bg-muted/40 p-3 dark:border-white/10 dark:bg-white/5">
                  <div>
                    <p className="text-sm font-medium text-foreground dark:text-white">
                      {formatAccountTransactionType(tx.type)}
                    </p>
                    <p className="text-xs text-muted-foreground dark:text-white/50">
                      {new Date(tx.createdAt).toLocaleString()}
                    </p>
                    {destination && (
                      <p className="text-xs text-muted-foreground dark:text-white/60">
                        {t("accounts.withdrawDestinationLabel")}: {destination}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <div
                      className={`text-sm font-semibold ${
                        isPositive ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
                      }`}>
                      {isPositive ? "+" : "-"}
                      {tx.amount.toFixed(2)} $
                    </div>
                    {tx.type === "withdrawal" && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          tx.status === "pending"
                            ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                            : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                        }`}>
                        {tx.status === "pending"
                          ? t("accounts.transactionStatusPending")
                          : t("accounts.transactionStatusCompleted")}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {totalAccountTransactionsPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm">
            <button
              type="button"
              aria-label={t("accounts.pagination.previous")}
              onClick={() =>
                setAccountTransactionsPage((prev) => Math.max(1, prev - 1))
              }
              disabled={accountTransactionsPage === 1}
              className="rounded-full border border-border bg-muted/60 px-3 py-1 text-muted-foreground transition hover:border-border hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:border-white/30 dark:hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40">
              {"<"}
            </button>
            <span className="text-muted-foreground dark:text-white/70">
              {accountTransactionsPage} / {totalAccountTransactionsPages}
            </span>
            <button
              type="button"
              aria-label={t("accounts.pagination.next")}
              onClick={() =>
                setAccountTransactionsPage((prev) =>
                  Math.min(totalAccountTransactionsPages, prev + 1)
                )
              }
              disabled={accountTransactionsPage === totalAccountTransactionsPages}
              className="rounded-full border border-border bg-muted/60 px-3 py-1 text-muted-foreground transition hover:border-border hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:border-white/30 dark:hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40">
              {">"}
            </button>
          </div>
        )}
      </div>

      {/* Modal de retrait */}
      <WithdrawFormModal
        open={withdrawModalOpen}
        onClose={() => setWithdrawModalOpen(false)}
        onConfirm={handleWithdraw}
        availableBalance={availableMainBalance}
        isProcessing={withdrawing}
      />

      {/* Modal de recharge */}
      <DepositFormModal
        open={depositModalOpen}
        onClose={() => {
          setDepositModalOpen(false);
          setCurrentPayment(null);
          setConfirmationValue("");
        }}
        onConfirm={handleDeposit}
        initialAmount={50}
        initialCurrency="USD"
        isProcessing={isSubmittingPayment}
      />

      {stripePayment?.clientSecret && (
        <StripePaymentModal
          open={!!stripePayment}
          clientSecret={stripePayment.clientSecret}
          amount={stripePayment.amount}
          currency={stripePayment.currency}
          metadata={stripePayment.metadata}
          onClose={() => setStripePayment(null)}
          onSuccess={handleStripeSuccess}
        />
      )}

      {/* Modal de confirmation de paiement */}
      {currentPayment && (
        <Dialog
          open={!!currentPayment}
          onOpenChange={() => setCurrentPayment(null)}>
          <DialogContent className="max-h-[90vh] max-w-md overflow-auto bg-card text-foreground rounded-3xl border border-border mx-auto dark:bg-slate-950/95 dark:text-white dark:border-white/10">
            <DialogHeader className="space-y-1 text-center">
              <DialogTitle className="text-2xl font-semibold">
                {t("accounts.paymentInitTitle")}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {paymentInstructions}
              {currentPayment.status !== "confirmed" && (
                <div className="space-y-3">
                  {currentPayment.method === "card" && (
                    <div className="space-y-2">
                      <Label>{t("payment.confirm.codeLabel", "Code de vérification")}</Label>
                      <Input
                        type="text"
                        value={confirmationValue}
                        onChange={(event) =>
                          setConfirmationValue(event.target.value)
                        }
                        placeholder={t("payment.confirm.codePlaceholder", "Entrez le code de vérification")}
                        className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white"
                      />
                    </div>
                  )}
                  {currentPayment.method === "crypto" && (
                    <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 p-4">
                      <p className="text-sm text-cyan-800 dark:text-cyan-400">
                        {t(
                          "payment.confirm.cryptoInstructions",
                          "Confirmez votre paiement. Un code unique sera envoyé à votre manager pour validation."
                        )}
                      </p>
                    </div>
                  )}
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => {
                        setCurrentPayment(null);
                        setConfirmationValue("");
                      }}
                      disabled={isConfirmingPayment}>
                      Retour
                    </Button>
                    <Button
                      onClick={handleConfirmPayment}
                      disabled={isConfirmingPayment || (currentPayment.method === "card" && !confirmationValue.trim())}>
                      {isConfirmingPayment
                        ? t("accounts.processing")
                        : t("accounts.confirmPaymentButton")}
                    </Button>
                  </div>
                </div>
              )}
              {currentPayment.status === "confirmed" && (
                <div className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 p-4 text-center">
                  <p className="text-emerald-700 dark:text-emerald-400 font-medium">
                    ✅ Paiement confirmé
                  </p>
                  <p className="text-sm text-muted-foreground dark:text-white/60 mt-1">
                    Votre compte principal a été crédité de{" "}
                    {currentPayment.amount} {currentPayment.currency}
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de transfert */}
      <Dialog open={transferModalOpen} onOpenChange={setTransferModalOpen}>
        <DialogContent className="max-w-md bg-card text-foreground rounded-3xl border border-border mx-auto dark:bg-slate-950/95 dark:text-white dark:border-white/10">
          <DialogHeader className="space-y-1 text-center">
            <DialogTitle className="text-2xl font-semibold">
              {t("accountsTransfers.modalTitle")}
            </DialogTitle>
            <p className="text-sm text-muted-foreground dark:text-white/60">
              {t("accountsTransfers.modalSubtitle")}
            </p>
          </DialogHeader>
          <form onSubmit={handleTransfer} className="mt-4 space-y-6">
            <div className="space-y-2">
              <Label>{t("accountsTransfers.directionLabel")}</Label>
              <Select
                value={transferForm.direction}
                onValueChange={(value) =>
                  setTransferForm((prev) => ({
                    ...prev,
                    direction: value as "main_to_trading" | "trading_to_main",
                  }))
                }>
                <SelectTrigger className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main_to_trading">
                    {t("accountsTransfers.directionMainToTrading")}
                  </SelectItem>
                  <SelectItem value="trading_to_main">
                    {t("accountsTransfers.directionTradingToMain")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("accountsTransfers.amountLabel")}</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={transferForm.amount}
                onChange={(event) =>
                  setTransferForm((prev) => ({
                    ...prev,
                    amount: event.target.value,
                  }))
                }
                placeholder={t("accountsTransfers.amountPlaceholder")}
                required
                className="bg-background text-foreground border-border dark:bg-white/5 dark:text-white"
                max={
                  transferForm.direction === "main_to_trading"
                    ? availableMainBalance
                    : availableTradingBalance
                }
              />
              <p className="text-xs text-muted-foreground dark:text-white/60">
                {t("accountsTransfers.availableLabel", {
                  amount:
                    transferForm.direction === "main_to_trading"
                      ? availableMainBalance.toFixed(2)
                      : availableTradingBalance.toFixed(2),
                })}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setTransferModalOpen(false)}
                disabled={transferring}>
                {t("accountsTransfers.cancel")}
              </Button>
              <Button type="submit" disabled={transferring}>
                {transferring
                  ? t("accountsTransfers.submitting")
                  : t("accountsTransfers.submit")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Historique des transferts (principal ↔ trading) */}
      <div
        ref={transfersSectionRef}
        className="rounded-3xl border border-border bg-muted/50 p-6 text-foreground dark:border-white/10 dark:bg-black/30 dark:text-white">
        <h3 className="mb-4 text-xl font-semibold">
          {t("accountsTransfers.historyTitle")}
        </h3>
        {transfers.length === 0 ? (
          <p className="text-sm text-muted-foreground dark:text-white/60">
            {t("accountsTransfers.historyEmpty")}
          </p>
        ) : (
          <>
            <div className="space-y-2">
              {paginatedTransfers.map((transfer) => (
                <div
                  key={transfer.id}
                  className="flex items-center justify-between rounded-lg border border-border bg-muted/40 p-3 dark:border-white/10 dark:bg-white/5">
                  <div>
                    <p className="text-sm font-medium">
                      {t("accountsTransfers.historyItem", {
                        amount: transfer.amount.toFixed(2),
                      })}
                    </p>
                    <p className="text-xs text-muted-foreground dark:text-white/60">
                      {new Date(transfer.createdAt).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">
                      {transfer.amount.toFixed(2)} $
                    </p>
                    <p
                      className={`text-xs ${
                        transfer.status === "completed"
                          ? "text-emerald-400"
                          : transfer.status === "failed"
                          ? "text-rose-400"
                          : "text-yellow-400"
                      }`}>
                      {transfer.status === "completed"
                        ? t("accountsTransfers.statusCompleted")
                        : transfer.status === "failed"
                        ? t("accountsTransfers.statusFailed")
                        : t("accountsTransfers.statusPending")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {transfers.length > transfersPerPage && (
              <div className="mt-4 border-t border-border pt-3 dark:border-white/10">
                <Pagination
                  currentPage={transfersPage}
                  totalPages={totalTransfersPages}
                  totalItems={transfers.length}
                  itemsPerPage={transfersPerPage}
                  onPageChange={setTransfersPage}
                  ariaLabelPrevious={t("accountsTransfers.pagination.previous")}
                  ariaLabelNext={t("accountsTransfers.pagination.next")}
                />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default AccountsPage;
